import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { supabase, createTimelineEvent } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SidebarLayout } from "@/components/SidebarLayout";
import { Home, ArrowLeft, LogOut, FileText, MessageSquare, Clock, Users, DollarSign, ExternalLink, Pencil, Link, Info } from "lucide-react";
import { toast } from "sonner";
import { HomeLensLogo } from "@/components/HomeLensLogo";
import { LifecycleTracker } from "@/components/homeflow/LifecycleTracker";
import { DocumentVault } from "@/components/homeflow/DocumentVault";
import { ChatPanel } from "@/components/homeflow/ChatPanel";
import { TimelinePanel } from "@/components/homeflow/TimelinePanel";
import { PartiesPanel } from "@/components/homeflow/PartiesPanel";
import { OffersPanel } from "@/components/homeflow/OffersPanel";
import { KeyDatesCard } from "@/components/homeflow/KeyDatesCard";
import { PreCloseChecklist } from "@/components/homeflow/PreCloseChecklist";
import { TransactionChecklist } from "@/components/homeflow/TransactionChecklist";
import { ListingActivityPanel } from "@/components/homeflow/ListingActivityPanel";

export default function TransactionDetailPage() {
  const { transactionId } = useParams();
  const navigate = useNavigate();
  const { user, profile, logout, displayName, displayEmail, displayRole } = useAuth();
  const [transaction, setTransaction] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [documentCount, setDocumentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [dotloopDialogOpen, setDotloopDialogOpen] = useState(false);
  const [dotloopInput, setDotloopInput] = useState("");
  const [dotloopSaving, setDotloopSaving] = useState(false);

  const isAgent = participants.some(p => p.user_id === user?.id && p.role === 'agent');
  const isBuyer = participants.some(p => p.user_id === user?.id && p.role === 'buyer');
  const isSeller = participants.some(p => p.user_id === user?.id && p.role === 'seller');
  const currentUserRole = participants.find(p => p.user_id === user?.id)?.role;

  // Checklist visibility: agent sees based on agent_side, buyer/seller see their own
  const agentSide = transaction?.agent_side || "both";
  const canSeeSeller = isSeller || (isAgent && (agentSide === "seller" || agentSide === "both"));
  const canSeeBuyer = isBuyer || (isAgent && (agentSide === "buyer" || agentSide === "both"));

  useEffect(() => {
    if (transactionId && user) {
      fetchTransaction();
      fetchParticipants();
      fetchDocumentCount();
    }
  }, [transactionId, user]);

  const fetchTransaction = async () => {
    console.log('[TxnDetail] fetchTransaction start, id:', transactionId);
    try {
      let txnTimeoutId;
      const result = await Promise.race([
        supabase.from('transactions').select('*').eq('id', transactionId).single().then(r => { clearTimeout(txnTimeoutId); return r; }),
        new Promise(resolve => {
          txnTimeoutId = setTimeout(() => {
            console.warn('[TxnDetail] Query timed out (8s)');
            resolve({ data: null, error: { message: 'timeout' } });
          }, 8000);
        })
      ]);

      console.log('[TxnDetail] result:', { data: !!result.data, error: result.error || 'none' });

      if (result.error) {
        console.error('[TxnDetail] Fetch failed:', result.error);
        toast.error("Failed to load transaction");
        navigate("/dashboard");
        return;
      }
      setTransaction(result.data);
    } catch (error) {
      console.error('[TxnDetail] fetchTransaction THREW:', error);
      toast.error("Failed to load transaction");
      navigate("/dashboard");
    }
    setLoading(false);
    console.log('[TxnDetail] loading=false');
  };

  const fetchParticipants = async () => {
    try {
      const { data, error } = await supabase
        .from('transaction_participants')
        .select(`
          *,
          profiles:user_id (
            id,
            full_name,
            email
          )
        `)
        .eq('transaction_id', transactionId);
      
      if (error) throw error;
      setParticipants(data || []);
    } catch (error) {
      console.error('Error fetching participants:', error);
    }
  };

  const fetchDocumentCount = async () => {
    try {
      const { count, error } = await supabase
        .from('documents')
        .select('*', { count: 'exact', head: true })
        .eq('transaction_id', transactionId);
      
      if (error) throw error;
      setDocumentCount(count || 0);
    } catch (error) {
      console.error('Error fetching document count:', error);
    }
  };

  const handleStageUpdate = async (newStage) => {
    if (!isAgent) {
      toast.error("Only agents can change the transaction stage");
      return;
    }

    try {
      const oldStage = transaction.stage;

      // Auto-set listing_date on pre_listing -> offer_negotiation if not already set.
      // Pre-Listing only exists for seller-side, so this is inherently seller-only.
      const update = { stage: newStage };
      let nowIso = null;
      if (
        oldStage === "pre_listing" &&
        newStage === "offer_negotiation" &&
        !transaction.listing_date
      ) {
        nowIso = new Date().toISOString();
        update.listing_date = nowIso;
      }

      const { error } = await supabase
        .from('transactions')
        .update(update)
        .eq('id', transactionId);
      
      if (error) throw error;
      
      // Create timeline event
      await createTimelineEvent(
        transactionId,
        'stage_changed',
        `Stage changed from ${oldStage} to ${newStage}`
      );
      
      setTransaction({ ...transaction, ...update });
      toast.success(`Stage updated to ${newStage}`);
    } catch (error) {
      console.error('Error updating stage:', error);
      toast.error("Failed to update stage");
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('[Logout] Error:', error);
    }
    navigate("/");
    toast.success("Logged out successfully");
  };

  const openDotloopDialog = () => {
    setDotloopInput(transaction?.dotloop_url || "");
    setDotloopDialogOpen(true);
  };

  const handleSaveDotloopUrl = async () => {
    const url = dotloopInput.trim();
    if (url && !url.includes("dotloop.com")) {
      toast.error("Please enter a valid DotLoop URL");
      return;
    }
    setDotloopSaving(true);
    try {
      const { error } = await supabase
        .from("transactions")
        .update({ dotloop_url: url || null })
        .eq("id", transactionId);
      if (error) throw error;
      setTransaction((prev) => ({ ...prev, dotloop_url: url || null }));
      setDotloopDialogOpen(false);
      toast.success(url ? "DotLoop link saved" : "DotLoop link removed");
    } catch (err) {
      console.error("[DotLoop] Save error:", err);
      toast.error("Failed to save link");
    } finally {
      setDotloopSaving(false);
    }
  };

  const handleToggleApplicable = async (key) => {
    if (!isAgent) return;
    const colName = key === "inspection_remedy" ? "inspection_remedy_applicable" : "appraisal_applicable";
    const label = key === "inspection_remedy" ? "Inspection & Remedy" : "Appraisal";
    const newVal = !(transaction[colName] !== false);
    try {
      const { error } = await supabase
        .from("transactions")
        .update({ [colName]: newVal })
        .eq("id", transactionId);
      if (error) throw error;
      setTransaction((prev) => ({ ...prev, [colName]: newVal }));
      toast.success(newVal ? `${label} marked as applicable` : `${label} marked as not applicable`);
    } catch (err) {
      console.error("[Toggle] Error:", err);
      toast.error("Failed to update");
    }
  };

  const handleAgentSideChange = async (newSide) => {
    try {
      const { error } = await supabase
        .from("transactions")
        .update({ agent_side: newSide })
        .eq("id", transactionId);
      if (error) throw error;
      setTransaction((prev) => ({ ...prev, agent_side: newSide }));
      const labels = { buyer: "Buyer side", seller: "Seller side", both: "Both sides" };
      toast.success(`Agent represents: ${labels[newSide]}`);
    } catch (err) {
      console.error("[AgentSide] Error:", err);
      toast.error("Failed to update");
    }
  };

  const formatPrice = (price) => {
    const num = typeof price === 'string' ? parseFloat(price.replace(/[$,]/g, '')) : price;
    if (isNaN(num) || num == null) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(num);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!transaction) return null;

  const fullAddress = `${transaction.property_address}, ${transaction.city}, ${transaction.state} ${transaction.zip}`;

  // Map active tab to sidebar nav highlight
  const tabToNav = { overview: "transactions", parties: "team-directory", documents: "documents", offers: "offers", chat: "messages", timeline: "transactions" };
  const sidebarActiveNav = tabToNav[activeTab] || "transactions";

  return (
    <SidebarLayout activeNav={sidebarActiveNav} onTabNavigate={setActiveTab} transactionId={transactionId} transactionRole={currentUserRole}>
      {/* Breadcrumbs */}
      <nav className="text-[13px] text-slate-400 mb-8" data-testid="breadcrumbs">
        <button onClick={() => navigate("/dashboard")} className="hover:text-slate-600 transition-colors">Transactions</button>
        <span className="mx-2 text-slate-300">/</span>
        <span className="text-slate-700 font-medium">{transaction.property_address}</span>
      </nav>

      {/* Transaction Header */}
      <div className="mb-10">
        <h1 className="font-heading text-3xl lg:text-[2.25rem] font-bold text-slate-900 tracking-tight leading-tight" data-testid="transaction-address">
          {transaction.property_address}
        </h1>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-slate-400 mt-2">
          <span>{transaction.city}, {transaction.state} {transaction.zip}</span>
          <span className="text-slate-200">|</span>
          <span className="capitalize">{transaction.property_type.replace('_', ' ')}</span>
          <span className="text-slate-200">|</span>
          <span className="font-semibold text-slate-900 text-sm">{formatPrice(transaction.price)}</span>
        </div>
      </div>

      {/* Lifecycle Tracker */}
      <LifecycleTracker 
        currentStage={transaction.stage}
        onStageChange={isAgent ? handleStageUpdate : undefined}
        canEdit={isAgent}
        applicability={{
          inspection_remedy: transaction.inspection_remedy_applicable !== false,
          appraisal: transaction.appraisal_applicable !== false,
        }}
        onToggleApplicable={isAgent ? handleToggleApplicable : undefined}
        showPreListing={canSeeSeller}
      />

      {/* Key Dates — shown for in_contract through closing */}
      {["in_contract", "inspection_remedy", "appraisal", "closing"].includes(transaction.stage) && (
        <KeyDatesCard
          transaction={transaction}
          onUpdate={(dates) => setTransaction((prev) => ({ ...prev, ...dates }))}
          isAgent={isAgent}
        />
      )}

      {/* Pre-Close Checklist — closing stage only, legacy fallback */}
      {transaction.stage === "closing" && canSeeSeller && !transaction.checklists?.pre_close_seller && (
        <PreCloseChecklist
          transactionId={transactionId}
          initialChecklist={transaction.pre_close_checklist}
        />
      )}

      {/* Role-based Checklists */}
      {/* Seller Pre-List: pre_listing stage (seller-side only) */}
      {transaction.stage === "pre_listing" && canSeeSeller && (
        <TransactionChecklist
          transactionId={transactionId}
          checklistKey="pre_list_seller"
          checklists={transaction.checklists}
          onUpdate={(update) => setTransaction((prev) => ({ ...prev, ...update }))}
        />
      )}

      {/* Listing Activity: offer_negotiation stage (seller-side only) */}
      {transaction.stage === "offer_negotiation" && canSeeSeller && (
        <ListingActivityPanel
          transaction={transaction}
          transactionId={transactionId}
          isAgent={isAgent}
          onUpdate={(update) => setTransaction((prev) => ({ ...prev, ...update }))}
        />
      )}

      {/* Seller Pre-Close: closing stage */}
      {transaction.stage === "closing" && canSeeSeller && (
        <TransactionChecklist
          transactionId={transactionId}
          checklistKey="pre_close_seller"
          checklists={transaction.checklists}
          onUpdate={(update) => setTransaction((prev) => ({ ...prev, ...update }))}
        />
      )}

      {/* Buyer Pre-Close: closing stage */}
      {transaction.stage === "closing" && canSeeBuyer && (
        <TransactionChecklist
          transactionId={transactionId}
          checklistKey="pre_close_buyer"
          checklists={transaction.checklists}
          onUpdate={(update) => setTransaction((prev) => ({ ...prev, ...update }))}
        />
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-center gap-3 my-6" data-testid="info-banner">
        <Info className="w-4 h-4 text-blue-600 shrink-0" />
        <p className="text-sm text-blue-800">
          This page reflects the latest status of your transaction. Navigate using the tabs below.
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6 lg:w-auto lg:inline-grid bg-slate-50 border border-slate-100">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <Home className="w-4 h-4 mr-2 hidden sm:block" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="parties" data-testid="tab-parties">
            <Users className="w-4 h-4 mr-2 hidden sm:block" />
            Parties
          </TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">
            <FileText className="w-4 h-4 mr-2 hidden sm:block" />
            Documents
          </TabsTrigger>
          <TabsTrigger value="offers" data-testid="tab-offers">
            <DollarSign className="w-4 h-4 mr-2 hidden sm:block" />
            Offers
          </TabsTrigger>
          <TabsTrigger value="chat" data-testid="tab-chat">
            <MessageSquare className="w-4 h-4 mr-2 hidden sm:block" />
            Chat
          </TabsTrigger>
          <TabsTrigger value="timeline" data-testid="tab-timeline">
            <Clock className="w-4 h-4 mr-2 hidden sm:block" />
            Timeline
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab — Two column layout matching mockup */}
        <TabsContent value="overview" className="space-y-0">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left column — 2/3 width */}
            <div className="lg:col-span-2 space-y-6">
              {/* Property Details Card */}
              <div className="bg-white rounded-lg border border-slate-100 shadow-soft p-6">
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-6">Property Details</h3>
                <div className="divide-y divide-slate-100">
                  <div className="flex justify-between items-baseline py-3.5 first:pt-0">
                    <span className="text-[13px] text-slate-400 tracking-wide">Address</span>
                    <span className="text-sm font-medium text-slate-800 text-right max-w-[280px]">{fullAddress}</span>
                  </div>
                  <div className="flex justify-between items-baseline py-3.5">
                    <span className="text-[13px] text-slate-400 tracking-wide">Type</span>
                    <span className="text-sm font-medium text-slate-800 capitalize">{transaction.property_type.replace('_', ' ')}</span>
                  </div>
                  <div className="flex justify-between items-baseline py-3.5">
                    <span className="text-[13px] text-slate-400 tracking-wide">Price</span>
                    <span className="text-sm font-semibold text-slate-900">{formatPrice(transaction.price)}</span>
                  </div>
                  <div className="flex justify-between items-center py-3.5">
                    <span className="text-[13px] text-slate-400 tracking-wide">Status</span>
                    <Badge variant="outline" className="capitalize text-xs font-medium">{transaction.stage.replace(/_/g, ' ')}</Badge>
                  </div>
                  <div className="flex justify-between items-baseline py-3.5">
                    <span className="text-[13px] text-slate-400 tracking-wide">Participants</span>
                    <span className="text-sm font-medium text-slate-800">{participants.length}</span>
                  </div>
                  <div className="flex justify-between items-baseline py-3.5 last:pb-0">
                    <span className="text-[13px] text-slate-400 tracking-wide">Documents</span>
                    <span className="text-sm font-medium text-slate-800">{documentCount}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column — 1/3 width */}
            <div className="lg:col-span-1 space-y-6">
              {/* Quick Actions Card */}
              <div className="bg-white rounded-lg border border-slate-100 shadow-soft p-6">
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-5">Quick Actions</h3>
                <div className="space-y-2">
                  <Button 
                    variant="outline" 
                    className="w-full justify-start h-11 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
                    onClick={() => setActiveTab("documents")}
                    data-testid="quick-action-documents"
                  >
                    <FileText className="w-[18px] h-[18px] mr-3 text-slate-400" strokeWidth={1.75} />
                    Upload Document
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start h-11 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
                    onClick={() => setActiveTab("chat")}
                    data-testid="quick-action-chat"
                  >
                    <MessageSquare className="w-[18px] h-[18px] mr-3 text-slate-400" strokeWidth={1.75} />
                    Open Chat
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start h-11 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
                    onClick={() => setActiveTab("parties")}
                    data-testid="quick-action-parties"
                  >
                    <Users className="w-[18px] h-[18px] mr-3 text-slate-400" strokeWidth={1.75} />
                    Manage Parties
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start h-11 border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
                    onClick={() => setActiveTab("offers")}
                    data-testid="quick-action-offers"
                  >
                    <DollarSign className="w-[18px] h-[18px] mr-3 text-slate-400" strokeWidth={1.75} />
                    View Offers
                  </Button>
                  {isAgent && (
                    <>
                      {transaction.dotloop_url ? (
                        <div className="flex items-center gap-1.5">
                          <a
                            href={transaction.dotloop_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1"
                            data-testid="quick-action-dotloop"
                          >
                            <Button variant="outline" className="w-full justify-start h-11 border-slate-200 text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition-all">
                              <ExternalLink className="w-[18px] h-[18px] mr-3 text-slate-400" strokeWidth={1.75} />
                              Open in DotLoop
                            </Button>
                          </a>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 shrink-0 text-slate-400"
                            onClick={openDotloopDialog}
                            title="Edit DotLoop link"
                            data-testid="dotloop-edit-btn"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          className="w-full justify-start h-11 border-slate-200 text-slate-500 hover:bg-slate-50 hover:border-slate-300 transition-all"
                          onClick={openDotloopDialog}
                          data-testid="dotloop-add-btn"
                        >
                          <Link className="w-[18px] h-[18px] mr-3 text-slate-400" strokeWidth={1.75} />
                          Add DotLoop Link
                        </Button>
                      )}

                      <Dialog open={dotloopDialogOpen} onOpenChange={setDotloopDialogOpen}>
                        <DialogContent className="sm:max-w-md" data-testid="dotloop-dialog">
                          <DialogHeader>
                            <DialogTitle className="text-base">{transaction.dotloop_url ? "Edit DotLoop Link" : "Add DotLoop Link"}</DialogTitle>
                            <DialogDescription className="text-sm">
                              Paste the DotLoop URL for this transaction.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 pt-2">
                            <Input
                              placeholder="https://www.dotloop.com/m/..."
                              value={dotloopInput}
                              onChange={(e) => setDotloopInput(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") handleSaveDotloopUrl(); }}
                              data-testid="dotloop-url-input"
                            />
                            <div className="flex justify-end gap-2">
                              <Button variant="ghost" size="sm" onClick={() => setDotloopDialogOpen(false)}>Cancel</Button>
                              <Button size="sm" onClick={handleSaveDotloopUrl} disabled={dotloopSaving} data-testid="dotloop-save-btn">
                                {dotloopSaving ? "Saving..." : "Save"}
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </>
                  )}
                </div>
              </div>
              {/* Agent Side Selector — agent only */}
              {isAgent && (
                <div className="bg-white rounded-lg border border-slate-100 shadow-soft p-6" data-testid="agent-side-card">
                  <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3">Agent Represents</h3>
                  <div className="flex gap-1.5">
                    {[
                      { value: "seller", label: "Seller" },
                      { value: "buyer", label: "Buyer" },
                      { value: "both", label: "Both" },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => handleAgentSideChange(opt.value)}
                        className={`flex-1 py-2 px-3 rounded-md text-xs font-medium transition-colors ${
                          agentSide === opt.value
                            ? "bg-[#1A2B4F] text-white"
                            : "bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                        }`}
                        data-testid={`agent-side-${opt.value}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Parties Tab */}
        <TabsContent value="parties">
          <PartiesPanel 
            transactionId={transactionId}
            participants={participants}
            onUpdate={fetchParticipants}
            isAgent={isAgent}
          />
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents">
          <DocumentVault 
            transactionId={transactionId}
            onDocumentUploaded={fetchDocumentCount}
          />
        </TabsContent>

        {/* Offers Tab */}
        <TabsContent value="offers">
          <OffersPanel transactionId={transactionId} />
        </TabsContent>

        {/* Chat Tab */}
        <TabsContent value="chat">
          <ChatPanel 
            transactionId={transactionId}
          />
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <TimelinePanel 
            transactionId={transactionId}
          />
        </TabsContent>
      </Tabs>
    </SidebarLayout>
  );
}
